import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { InfoOutlined } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { useAppDispatch, useTypedSelector } from "../../app/store";
import {
  clearTwoFactor,
  disableTwoFactor,
  resetAdministratorPassword,
  resolveTwoFactor,
} from "./securitySlice";
import {
  ResetPasswordDialog,
  ResetPasswordDialogValues,
} from "./components/ResetPasswordDialog";
import { EnableTwoFactorDialog } from "./components/EnableTwoFactorDialog";
import { DisableTwoFactorDialog } from "./components/DisableTwoFactorDialog";

export { AUTHENTICATOR_STORE_URLS } from "./components/EnableTwoFactorDialog";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3, 2),
    },
    section: {
      marginTop: theme.spacing(3),
    },
    sectionTitle: {
      marginBottom: theme.spacing(1.5),
    },
    twoFactorDescription: {
      marginBottom: theme.spacing(2),
    },
    loginInfo: {
      margin: theme.spacing(1, 0),
    },
    loginInfoTitle: {
      marginBottom: theme.spacing(0.5),
    },
    loginInfoList: {
      margin: theme.spacing(0.5, 0, 0),
      paddingLeft: theme.spacing(3),
    },
    sectionButton: {
      marginTop: theme.spacing(1),
    },
  })
);

function TwoFactorLoginInfo() {
  const classes = useStyles();
  return (
    <Alert className={classes.loginInfo} icon={<InfoOutlined />} severity="info">
      <Typography className={classes.loginInfoTitle} variant="body2">
        二要素認証でのログイン方法
      </Typography>
      <ol className={classes.loginInfoList}>
        <li>通常のログインフォームに必要事項を入力し、「ログイン」を押します。</li>
        <li>6桁のワンタイムパスワードを入力するフォームが表示されます。</li>
        <li>指定の認証アプリから取得したワンタイムパスワードを入力し、「ログイン」を押します。</li>
      </ol>
    </Alert>
  );
}

export const SecurityPage: React.VFC<{}> = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const state = useTypedSelector((x) => ({
    login: x.login,
    security: x.security,
  }));
  const [openReset, setOpenReset] = useState(false);
  const [openEnable, setOpenEnable] = useState(false);
  const [openDisable, setOpenDisable] = useState(false);

  const resolveTwoFactorState = useCallback(() => {
    if (state.login.accessToken) {
      dispatch(resolveTwoFactor({ accessToken: state.login.accessToken }));
    }
  }, [dispatch, state.login.accessToken]);

  useEffect(() => {
    resolveTwoFactorState();
  }, [resolveTwoFactorState]);

  const handleResetSubmit = useCallback(
    async (values: ResetPasswordDialogValues) => {
      if (!state.login.accessToken) {
        return;
      }
      await dispatch(
        resetAdministratorPassword({
          accessToken: state.login.accessToken,
          newPassword: values.newPassword,
          newPasswordConfirmation: values.newPasswordConfirmation,
        })
      );
      setOpenReset(false);
    },
    [dispatch, state.login.accessToken]
  );

  const handleEnableClose = useCallback(() => {
    setOpenEnable(false);
    dispatch(clearTwoFactor());
    resolveTwoFactorState();
  }, [dispatch, resolveTwoFactorState]);

  const handleDisableSubmit = useCallback(async () => {
    if (!state.login.accessToken) {
      return;
    }
    const result = await dispatch(
      disableTwoFactor({ accessToken: state.login.accessToken })
    );
    if (disableTwoFactor.fulfilled.match(result)) {
      setOpenDisable(false);
      resolveTwoFactorState();
    }
  }, [dispatch, resolveTwoFactorState, state.login.accessToken]);

  const busy =
    state.security.twoFactorStatus === "enabling" ||
    state.security.twoFactorStatus === "disabling";
  const loading =
    state.security.twoFactorStatus !== "failed" &&
    (state.security.twoFactorEnabled === null ||
      state.security.twoFactorStatus === "loading");
  const loadFailed = state.security.twoFactorStatus === "failed";

  return (
    <>
      <Paper className={classes.root}>
        <Typography className={classes.sectionTitle} variant="h5" component="h3">
          大臣AXクラウドシステム管理者
        </Typography>
        <Typography>
          大臣AXクラウドのシステム管理者(Administrator)のパスワードを忘れてしまった場合、<br />
          ここでパスワードをリセットできます。
        </Typography>
        <Button color="secondary" variant="contained" onClick={() => setOpenReset(true)}>
          パスワードをリセット
        </Button>
      </Paper>

      <Paper className={`${classes.root} ${classes.section}`}>
        <Typography className={classes.sectionTitle} variant="h5" component="h3">
          二要素認証
        </Typography>
        {loading ? (
          <CircularProgress size={24} />
        ) : loadFailed ? (
          <>
            <Typography className={classes.twoFactorDescription}>
              二要素認証の状態を取得できませんでした。
            </Typography>
            <Button
              className={classes.sectionButton}
              color="primary"
              variant="contained"
              onClick={resolveTwoFactorState}
            >
              再試行
            </Button>
          </>
        ) : state.security.twoFactorEnabled ? (
          <>
            <Typography className={classes.twoFactorDescription}>
              二要素認証は設定済みです
            </Typography>
            <Typography className={classes.twoFactorDescription}>
              このアカウントにログインするには、二要素認証の設定に使用したモバイル端末の認証アプリに表示されているワンタイムパスワードが必要です。
            </Typography>
            <TwoFactorLoginInfo />
            <Button
              className={classes.sectionButton}
              color="secondary"
              variant="contained"
              onClick={() => setOpenDisable(true)}
              disabled={busy}
            >
              二要素認証の解除
            </Button>
          </>
        ) : (
          <>
            <Typography className={classes.twoFactorDescription}>
              二要素認証を有効化することによって、アカウントを悪意のある他ユーザーの不正利用から守ります。
            </Typography>
            <TwoFactorLoginInfo />
            <Button
              className={classes.sectionButton}
              color="primary"
              variant="contained"
              onClick={() => {
                setOpenEnable(true);
              }}
              disabled={busy || !state.security.twoFactorSetupInfo}
            >
              二要素認証を設定する
            </Button>
          </>
        )}
      </Paper>

      <ResetPasswordDialog
        open={openReset}
        onClose={() => setOpenReset(false)}
        onSubmit={handleResetSubmit}
      />
      {state.login.accessToken && (
        <EnableTwoFactorDialog
          open={openEnable}
          accessToken={state.login.accessToken}
          setupInfo={state.security.twoFactorSetupInfo}
          status={state.security.twoFactorStatus}
          onClose={handleEnableClose}
        />
      )}
      <DisableTwoFactorDialog
        open={openDisable}
        busy={state.security.twoFactorStatus === "disabling"}
        onClose={() => setOpenDisable(false)}
        onSubmit={handleDisableSubmit}
      />
    </>
  );
};
